<template lang="html">
  <div class="catalog-showcase relative">
    <!-- Sticky utility strip: sort always; mobile filter entry -->
    <div
      key="header"
      class="catalog-showcase__header sticky top-(--ui-header-height) z-30 mb-3 flex items-center justify-between gap-3 rounded-md border border-default bg-default px-3 py-2 shadow-xs"
    >
      <UButton
        class="lg:hidden"
        color="neutral"
        variant="outline"
        size="sm"
        icon="i-lucide-sliders-horizontal"
        :label="filtersButtonLabel"
        @click="filtersOpen = true"
      />

      <CatalogSortSelector
        v-model="currentSort"
        class="ml-auto shrink-0"
      />
    </div>

    <div class="catalog-showcase__body flex items-start gap-4">
      <!-- Desktop in-category filters (D14) — stock toggle only; no invented facets -->
      <aside
        class="catalog-showcase__sidebar hidden w-56 shrink-0 lg:block xl:w-60"
        aria-label="Фильтры"
      >
        <div
          class="sticky top-[calc(var(--ui-header-height)+3.25rem)] space-y-4 rounded-md border border-default bg-default p-4 shadow-xs"
        >
          <h2 class="text-sm font-semibold text-highlighted">
            Фильтры
          </h2>

          <UCheckbox
            v-model="outOfStockModel"
            label="Скрыть отсутствующие"
            class="text-sm"
          />

          <UButton
            v-if="hasActiveInCategoryFilters"
            color="neutral"
            variant="ghost"
            size="xs"
            class="w-full justify-start"
            @click="resetInCategoryFilters"
          >
            Сбросить фильтры
          </UButton>
        </div>
      </aside>

      <div class="catalog-showcase__main min-w-0 flex-1">
        <div
          v-if="items.length"
          key="list"
          class="catalog-showcase__list grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2 sm:gap-2.5 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] md:gap-3"
        >
          <ProductCard
            v-for="(product, index) in items"
            :key="product.id"
            :value="product"
            :category="category"
            :eager="index < EAGER_CARDS"
          />
        </div>

        <slot
          v-if="!items.length && !pending"
          name="empty"
          :reset="resetInCategoryFilters"
          :has-active-filters="hasActiveInCategoryFilters"
        >
          <NonePanel />
        </slot>

        <div
          v-if="pending"
          class="catalog-showcase__loader flex justify-center py-6"
        >
          <UIcon
            name="i-lucide-loader-circle"
            class="animate-spin text-2xl text-neutral-400"
          />
        </div>

        <!-- Краулябельная пагинация: обычные <a> (не NuxtLink — переход должен
        перезагрузить листинг с курсором из URL), краулер обходит каталог по
        цепочке rel=next; пользователям контент догружает бесконечный скролл -->
        <nav
          v-if="nextHref || pagination.isPaginated.value"
          class="catalog-showcase__pagination flex items-center justify-center gap-6 py-4"
        >
          <a
            v-if="pagination.isPaginated.value"
            :href="route.path"
            class="text-sm text-muted underline-offset-4 hover:underline"
          >← В начало каталога</a>
          <a
            v-if="nextHref"
            :href="nextHref"
            rel="next"
            class="text-sm text-muted underline-offset-4 hover:underline"
          >Следующая страница →</a>
        </nav>
      </div>
    </div>

    <div
      ref="sentinelRef"
      class="catalog-showcase__sentinel absolute right-0 bottom-0 left-0 h-1"
    />

    <USlideover
      v-model:open="filtersOpen"
      side="left"
      title="Фильтры"
    >
      <template #body>
        <div class="flex flex-col gap-4">
          <UCheckbox
            v-model="outOfStockModel"
            label="Скрыть отсутствующие"
            class="text-sm"
          />

          <UButton
            v-if="hasActiveInCategoryFilters"
            color="neutral"
            variant="outline"
            size="sm"
            @click="resetInCategoryFilters"
          >
            Сбросить фильтры
          </UButton>
        </div>
      </template>

      <template #footer>
        <UButton
          color="primary"
          block
          label="Закрыть"
          @click="filtersOpen = false"
        />
      </template>
    </USlideover>
  </div>
</template>

<script setup lang="ts">
import {
  ProductListItemDto,
  type EntityRef,
  type ProductsListGetRequestQuery,
} from '@repo/api/web/models'
import { useIntersectionObserver } from '@vueuse/core'

import CatalogSortSelector from './CatalogSortSelector.vue'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import ProductCard from '#Products/components/Card/ProductCard.vue'
import {
  LISTING_SORT_CARRY_PARAMS,
  listingSortFromQuery,
  listingSortToQuery,
  pickListingCarryQuery,
  type ListingSortValue,
} from '#Products/support/listingSortUrl'
import NonePanel from '~/app/components/None/NonePanel.vue'

const props = withDefaults(
  defineProps<{
    category?: EntityRef
    searchQuery?: string
  }>(),
  {},
)

const emit = defineEmits<{
  'update:resultCount': [count: number]
  'update:hasMore': [hasMore: boolean]
}>()

const { service } = useProducts()
const { wellKnown } = await useAttributes()
const route = useRoute()
const router = useRouter()

// Первый ряд сетки (LCP-кандидаты) грузится сразу, остальное — lazy
const EAGER_CARDS = 4

const filtersOpen = ref(false)

const pagination = useListingPagination({
  carryParams: [...LISTING_SORT_CARRY_PARAMS],
})

const initialSort = listingSortFromQuery(route.query, wellKnown.value)

const { collection, pending, setFilter, load, hasAfter, nextPage, query, meta } = await useCursorListing<
  any,
  ProductListItemDto
>({
  key: 'CatalogShowcase' + (props.category?.id ?? props.searchQuery ?? ''),
  fetcher: (queryParams: ProductsListGetRequestQuery) =>
    service.list(queryParams),
  transform: data => ProductListItemDto.createFromCollection(data ?? []),
  initialQuery: {
    Limit: 24,
    Query: props.searchQuery,
    'Filter.OutOfStock': true,
    'Filter.CategoryId': props.category?.id,
    SortField: initialSort.sortField,
    SortDirection: initialSort.sortDirection,
    ...(pagination.cursorQuery.value ?? {}),
  },
})

const nextHref = computed(() => pagination.nextPageHref(meta.value))

/**
 * Смена сортировки/фильтра/категории перезапрашивает листинг с начала —
 * курсор из URL при этом устаревает (описывает слайс старой выдачи):
 * чистим параметры пагинации, сохраняя q/sort/dir.
 */
function dropPaginationFromUrl(extra: Record<string, string | undefined> = {}) {
  const nextQuery = pickListingCarryQuery(route.query, extra)
  const same
    = Object.keys(route.query).length === Object.keys(nextQuery).length
      && Object.keys(nextQuery).every(
        key => String(route.query[key] ?? '') === nextQuery[key],
      )

  if (!same) {
    router.replace({
      path: route.path,
      query: nextQuery,
    })
  }

  setFilter({ CursorField: undefined, CursorId: undefined })
}

const currentSort = ref<ListingSortValue>({ ...initialSort })
const sentinelRef = ref<HTMLElement | null>(null)
useIntersectionObserver(
  sentinelRef,
  ([entry]) => {
    if (entry?.isIntersecting && hasAfter.value && !pending.value) {
      nextPage()
    }
  },
  { threshold: 0.1 },
)

const items = computed(() => collection.value || [])

watch(
  [items, hasAfter, pending],
  () => {
    if (pending.value) return
    emit('update:resultCount', items.value.length)
    emit('update:hasMore', hasAfter.value)
  },
  { immediate: true },
)

// Ecommerce: показ списка (impressions). Отправляем только новые позиции
// (при infinite-scroll), с позицией в общем списке; list — категория/поиск/каталог
const ecommerce = useEcommerce()
const impressedProductIds = new Set<number>()
const impressionsList = computed(
  () => props.category?.name || (props.searchQuery ? 'Поиск' : 'Каталог'),
)

watch(items, (list) => {
  if (import.meta.server) return
  const fresh = list
    .map((product, index) => ({ product, position: index + 1 }))
    .filter(({ product }) => product.id != null && !impressedProductIds.has(product.id))
  if (!fresh.length) return

  fresh.forEach(({ product }) => impressedProductIds.add(product.id!))
  ecommerce.impressions(
    fresh.map(({ product, position }) =>
      mapCatalogProduct(product, { list: impressionsList.value, position }),
    ),
  )
}, { flush: 'post' })

const outOfStockModel = computed({
  get() {
    return !query.value['Filter.OutOfStock']
  },
  set(value: boolean) {
    dropPaginationFromUrl()
    setFilter({
      'Filter.OutOfStock': !value,
    })
    load(true)
  },
})

const hasActiveInCategoryFilters = computed(
  () => query.value['Filter.OutOfStock'] !== true,
)

const filtersButtonLabel = computed(() =>
  hasActiveInCategoryFilters.value ? 'Фильтры · 1' : 'Фильтры',
)

function resetInCategoryFilters() {
  dropPaginationFromUrl()
  setFilter({
    'Filter.OutOfStock': true,
  })
  load(true)
}

watch(currentSort, (newSort) => {
  const sortQuery = listingSortToQuery(newSort, wellKnown.value)
  dropPaginationFromUrl({
    sort: sortQuery.sort ?? '',
    dir: sortQuery.dir ?? '',
  })
  setFilter({
    SortField: newSort.sortField,
    SortDirection: newSort.sortDirection,
  })
  load(true)
}, { deep: true })

watch(
  () => props.category?.id,
  () => {
    dropPaginationFromUrl()
    setFilter({
      'Filter.CategoryId': props.category?.id,
      SortField: currentSort.value.sortField,
      SortDirection: currentSort.value.sortDirection,
    })
    load(true)
  },
)

watch(
  () => props.searchQuery,
  () => {
    dropPaginationFromUrl()
    setFilter({
      Query: props.searchQuery,
      SortField: currentSort.value.sortField,
      SortDirection: currentSort.value.sortDirection,
    })
    load(true)
  },
)
</script>
